/**
 * useCreativeRole — reads `profiles.creative_role` for the signed-in
 * user and coerces it into a known `CreativeRole` slug. Shared React
 * Query cache means every ONB-3 surface (Learn card ordering, Nova
 * chat chips, activation checklist priority, first-project prefills)
 * pays for the fetch once per session.
 *
 * A missing profile row, a null column, or an unknown free-text
 * value (from the `other` role) all coerce to `'other'` — so
 * consumers never have to null-check. The `role` return is always
 * a valid `CreativeRole` slug once loaded, and `personalization`
 * is the resolved `RolePersonalization` bundle for that slug.
 *
 * Only fires when `user?.id` is set. In demo mode we still fetch
 * — Preview Studio users have a real profile row — but if the row
 * doesn't exist yet (fresh signup pre-wizard), we fall back to the
 * `other` bundle so the empty state renders instead of blocking.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  coerceCreativeRole,
  getPersonalization,
  type CreativeRole,
  type RolePersonalization,
} from '@/lib/onboardingPersonalization';

export interface UseCreativeRoleResult {
  /** Coerced role slug — always valid, defaults to `'other'`. */
  role: CreativeRole;
  /** Fully-resolved personalization bundle for the role. */
  personalization: RolePersonalization;
  /** True while the first fetch is in-flight. */
  isLoading: boolean;
}

export function useCreativeRole(): UseCreativeRoleResult {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['creative-role', user?.id],
    enabled: !!user?.id,
    // The role rarely changes after onboarding — no need to refetch
    // aggressively. A single fetch per session is plenty.
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CreativeRole> => {
      if (!user?.id) return 'other';
      try {
        const { data } = await (supabase.from('profiles') as any)
          .select('creative_role')
          .eq('id', user.id)
          .maybeSingle();
        return coerceCreativeRole(data?.creative_role);
      } catch {
        return 'other';
      }
    },
  });

  const role: CreativeRole = data ?? 'other';
  return {
    role,
    personalization: getPersonalization(role),
    isLoading,
  };
}

export default useCreativeRole;
